import dayjs from 'dayjs';
import { DefaultWidgetDateFormat } from './IDateWidget';

// Parte data delle varianti ISO 8601, estesa (1970-05-21[T...]) e compatta
// (19700521[T...]), col backreference a imporre separatori coerenti; lo
// spazio al posto della `T` è ammesso da RFC 3339 ed è frequente nelle API
// di terze parti. Il gruppo 5 è l'orario, con l'eventuale fuso in coda.
const isoDateRegex = /^(\d{4})(-?)(\d{2})\2(\d{2})(?:[T ](.+))?$/;

// Orario hh[mm[ss]] nelle due varianti; i decimali dei secondi ammettono
// punto e virgola (ISO preferisce la virgola). Restano due regex separate
// per contenere la complessità della singola espressione (Sonar S5843)
const extendedTimeRegex = /^(\d{2})(?::(\d{2})(?::(\d{2})(?:[.,](\d+))?)?)?$/;
const basicTimeRegex = /^(\d{2})(?:(\d{2})(?:(\d{2})(?:[.,](\d+))?)?)?$/;

// Fuso in coda all'orario: Z oppure ±hh[[:]mm]
const isoZoneRegex = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/;

/**
 * Porta in gg/mm/aaaa il valore di una data ISO 8601, mantenendo il valore
 * originale se non interpretabile come data (orario o fuso malformati
 * inclusi).
 *
 * I valori con fuso esplicito (`Z` o offset) individuano un istante, quindi
 * vengono convertiti a `Europe/Rome` prima di estrarre la data. Quelli senza
 * fuso sono date "di calendario": la parte data è presa così com'è, senza
 * conversione di fuso; l'orario è validato ma non sposta il giorno.
 */
export function formatIsoCalendarDate<T>(value: T): T | string {
  if (!value || typeof value !== 'string') return value;

  const trimmed = value.trim();
  const match = isoDateRegex.exec(trimmed);
  if (!match) {
    // Idempotenza sui valori già formattati (strict, richiede
    // `customParseFormat`, esteso dagli `initDayjs` al bootstrap)
    const formatted = dayjs(
      trimmed,
      DefaultWidgetDateFormat.datePickerFormat,
      true,
    );
    return formatted.isValid()
      ? formatted.format(DefaultWidgetDateFormat.datePickerFormat)
      : value;
  }

  // Il calendario va validato strict prima del parser nativo, che dei
  // giorni fuori mese fa il rollover (es. 31 febbraio → 3 marzo) invece
  // di rifiutarli
  const date = dayjs(`${match[1]}-${match[3]}-${match[4]}`, 'YYYY-MM-DD', true);
  if (!date.isValid()) return value;

  const time = match[5];
  if (!time) return date.format(DefaultWidgetDateFormat.datePickerFormat);

  const zone = isoZoneRegex.exec(time)?.[0];
  const instant = parseIsoInstant(
    date.format('YYYY-MM-DD'),
    zone ? time.slice(0, -zone.length) : time,
    zone,
  );
  if (!instant) return value;

  return (zone ? instant.tz('Europe/Rome') : date).format(
    DefaultWidgetDateFormat.datePickerFormat,
  );
}

/**
 * Interpreta data e orario ISO, con l'eventuale fuso (la conversione richiede
 * `utc` e `timezone`, estesi dagli `initDayjs` al bootstrap); `null` se
 * l'orario non è nella forma ISO o è fuori intervallo (es. `99:99`).
 */
function parseIsoInstant(
  isoDate: string,
  timeOfDay: string,
  zone: string | undefined,
): dayjs.Dayjs | null {
  const time =
    extendedTimeRegex.exec(timeOfDay) ?? basicTimeRegex.exec(timeOfDay);
  if (!time) return null;

  const [, hours, minutes = '00', seconds = '00', fraction] = time;
  const timestamp = `${isoDate}T${hours}:${minutes}:${seconds}`;

  // Senza fuso dayjs non usa il parser nativo ma costruisce la data dai
  // componenti, con rollover degli orari fuori intervallo: serve lo strict.
  // I decimali non spostano il giorno, la regex ne garantisce già la forma
  if (!zone) {
    const instant = dayjs(timestamp, 'YYYY-MM-DDTHH:mm:ss', true);
    return instant.isValid() ? instant : null;
  }

  // Normalizzazione alla sola forma estesa garantita dal parser nativo, che
  // rifiuta gli orari fuori intervallo: millisecondi a 3 cifre e offset
  // ±hh:mm
  const milliseconds = fraction
    ? `.${fraction.padEnd(3, '0').slice(0, 3)}`
    : '';
  const instant = dayjs(
    `${timestamp}${milliseconds}${normalizeIsoOffset(zone)}`,
  );
  return instant.isValid() ? instant : null;
}

// Porta il fuso in forma estesa: `Z` resta, `±hh` e `±hhmm` diventano
// `±hh:mm`
function normalizeIsoOffset(zone: string): string {
  if (zone === 'Z') return zone;
  const minutes = zone.length > 3 ? zone.slice(-2) : '00';
  return `${zone.slice(0, 3)}:${minutes}`;
}
